<script setup lang="ts">
  import { computed } from 'vue'
  import { cn } from '@rapoo/element/lib/utils'
  import type { KeyboardKeyProps, KeyboardEmits } from './types'

  const getOS = () => {
    const ua = navigator.userAgent.toLowerCase()
    if (ua.includes('windows')) return 'windows'
    if (ua.includes('macintosh') || ua.includes('mac os')) return 'macx'
    if (ua.includes('linux')) return 'linux'

    const platform = navigator.platform.toLowerCase()
    if (platform.startsWith('win')) return 'windows'
    if (platform.startsWith('mac')) return 'macx'
    if (platform.startsWith('linux') || platform.startsWith('x11')) return 'linux'

    return null
  }

  defineOptions({
    name: 'KeyboardKey',
  })

  const props = withDefaults(defineProps<KeyboardKeyProps>(), {
    nativeEventNames: () => [],
    disabled: false,
  })

  const emits = defineEmits<KeyboardEmits>()

  const nativeEventHandlers = computed<Record<string, (event: Event) => void>>(() => {
    const handlers: Record<string, (event: Event) => void> = {}

    if (props.nativeEventNames) {
      props.nativeEventNames.forEach((eventName) => {
        handlers[eventName] = (event: Event) => {
          emits('key-event', {
            data: props.config,
            eventName,
            nativeEvent: event,
          })
        }
      })
    }

    return handlers
  })

  const rootStyle = computed(() => ({
    left: `${props.config.layout?.left}px`,
    top: `${props.config.layout?.top}px`,
    width: `${props.config.layout?.width}px`,
    height: `${props.config.layout?.height}px`,
  }))

  const draggable = computed(() => props.nativeEventNames?.includes('dragstart') ?? false)

  const label = computed(() => {
    const {
      config: { name },
    } = props

    if (typeof name === 'string') return name
    if (!name) return ''

    const os = getOS()
    if (os) {
      const byOS = (name as any)[os]
      if (byOS) return byOS
    }

    const byCountry = (name as any)['standard']
    if (byCountry) return byCountry

    return ''
  })
</script>

<template>
  <button
    :class="
      cn(
        'group absolute border border-transparent flex items-center justify-center rounded-md cursor-pointer transition-colors text-s-medium text-black-white hover:text-text-glass-hover active:text-black disabled:text-text-disabled disabled:cursor-not-allowed',
        props.class,
      )
    "
    :style="[rootStyle, props.style]"
    :draggable="draggable"
    :disabled="props.disabled"
    v-on="nativeEventHandlers"
  >
    <slot name="label" :config="config">
      <div class="pointer-events-none flex items-center justify-center leading-none select-none">
        <span class="whitespace-pre-wrap text-center">{{ label }}</span>
      </div>
    </slot>
  </button>
</template>

<style scoped></style>
